/**
 * Ende-zu-Ende-Verschlüsselung (E21, 25.10.2026) — was verschlüsselt wird.
 *
 * Jede Zeile einer verschlüsselten Tabelle ist ein **Umschlag** (Kennung,
 * Konto, Datum/Uhrzeit, Zeitstempel — lesbar, damit der Server abgleichen und
 * abgrenzen kann) plus `payload`: die Inhaltsfelder als Chiffrat, das nur die
 * Geräte des Kontos öffnen können. Im Umschlag stehen statt der Inhalte die
 * leeren Werte aus `SEALED_EMPTY` — der Server prüft das (`isSealedEmpty`), damit
 * kein Gerät versehentlich Klartext neben das Chiffrat legt.
 *
 * Verschlüsselt wird auf dem Gerät beim Senden, entschlüsselt beim Holen
 * (`apps/web/src/sync/e2e.ts`). Lokal liegt Klartext — wie bisher.
 *
 * Neue Tabelle hier eintragen → Spalte `payload`, Push-Schema mit `payload`,
 * und der Test „Server sieht keinen Inhalt“ muss sie abdecken.
 */

/** Kennzeichen und Version des Chiffrats: `kwe1.` + Base64(IV ‖ AES-GCM). */
export const E2E_PAYLOAD_PREFIX = 'kwe1.';

/** Obergrenze für ein Chiffrat — ein Gedanke hat höchstens `NOTE_BODY_MAX` Zeichen. */
export const E2E_PAYLOAD_MAX = 120_000;

/**
 * Grund, mit dem der Server Klartext abweist, sobald das Konto verschlüsselt.
 * Kein Fehlversuch: Das Gerät hält die Änderung, bis es entsperrt ist.
 */
export const E2E_REQUIRED_REASON = 'Verschlüsselung nötig';

/**
 * Verschlüsseltes Bild (E21): `KWE1` ‖ IV (12) ‖ AES-GCM(JPEG). Der Server
 * prüft nur den Kopf und die Größe — den Inhalt kann er nicht mehr ansehen.
 */
export const SEALED_FILE_MAGIC = [0x4b, 0x57, 0x45, 0x31] as const; // „KWE1“
export const SEALED_FILE_CONTENT_TYPE = 'application/vnd.kraftwerk.sealed';
/** Kopf, IV und Prüfwert kommen zum JPEG dazu. */
export const SEALED_FILE_OVERHEAD = 4 + 12 + 16;

export function looksSealed(bytes: Uint8Array): boolean {
  return (
    bytes.length > SEALED_FILE_OVERHEAD && SEALED_FILE_MAGIC.every((byte, i) => bytes[i] === byte)
  );
}

/** Leere Werte der Inhaltsfelder im Umschlag, je Tabelle. */
export const SEALED_EMPTY = {
  journal_notes: {
    title: null,
    body: '',
    mood: null,
    tags: [] as string[],
    eventKind: null,
    eventId: null,
    eventTitle: null,
    eventDate: null,
  },
  // Gewicht und Schritte bleiben lesbar: Wochenrückblick und Kurzbefehle.
  journal_entries: {
    sleepHours: null,
    sleepQuality: null,
    energy: null,
    mood: null,
    soreness: null,
    note: null,
    measurements: null,
  },
  // Das Bild selbst verschlüsselt das Gerät vor dem Hochladen (`KWE1`-Datei).
  journal_photos: {
    caption: null,
  },
  recipe_cook_notes: {
    rating: null,
    note: null,
  },
  // Kosten (E17): privat mit dem Kontoschlüssel, geteilt mit dem
  // Haushaltsschlüssel. Lesbar bleiben Haushalt, Verweise, Datum, Zahler.
  expense_categories: {
    name: null,
    budgetCents: null,
  },
  recurring_expenses: {
    title: '',
    amountCents: 0,
  },
  expenses: {
    amountCents: 0,
    category: 'other',
    note: null,
  },
  savings_goals: {
    name: '',
    targetCents: 0,
  },
  savings_deposits: {
    amountCents: 0,
  },
  // Geteiltes Planen und Haushalt (26.10.2026). Lesbar bleibt, was der Server
  // braucht: Aufgaben ganz (Titel und Notiz stehen in der Mitteilung),
  // Erledigt-Einträge (Aufgaben-Geld), Vorrat (Vorbereitungs-Erinnerung), bei
  // Terminen Titel, Zeit, Ort und Notiz (Kalender-Abo, Timo 26.10.2026).
  task_lists: {
    name: '',
  },
  events: {
    activity: null,
    activityMin: null,
  },
  household_events: {
    activity: null,
    activityMin: null,
  },
  shopping_items: {
    name: '',
    amount: null,
    unit: null,
    category: 'other',
    note: null,
  },
  bucket_items: {
    title: '',
    note: null,
  },
  member_notes: {
    body: '',
  },
  // Training, Läden, Markierungen und Körperwerte (27.10.2026, PLANUNG.md
  // (6i)). Der Server liest davon nichts — geprüft in Scheduler, Kurzbefehlen,
  // Rückblick, Haushalt-Woche und Kalender. Leere Werte sind gültige Werte der
  // Spalten (`not null` mit Vorgabe), außer dort, wo leer „kaputt“ heißen soll.
  //
  // Übungen: lesbar bleiben Grundtyp (`parentId`, eine Ebene prüft der Server)
  // und Vorlagen-Kennung (`catalogKey`, „Vorlagen nachladen“ erkennt daran,
  // was schon da ist).
  exercises: {
    name: '',
    variantLabel: null,
    trackingMode: 'weightReps',
    equipment: 'other',
    primaryMuscles: [] as string[],
    secondaryMuscles: [] as string[],
    movementPattern: 'isolation',
    bodyRegion: 'fullBody',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 2.5,
    restSeconds: 90,
    videos: [] as unknown[],
    cues: [] as string[],
    setupNotes: null,
    note: null,
    isFavorite: false,
    sortOrder: 0,
  },
  // Lesbar bleiben nur die Verweise (Plan-Tag, Übung). `sets: 0` gibt es im
  // Klartext nicht (mindestens 1) — eine leere Hülle fällt so auf.
  plan_exercises: {
    position: 0,
    sets: 0,
    repMin: 0,
    repMax: 0,
    restSeconds: null,
    note: null,
    supersetWithPrevious: false,
  },
  // Geteilt (immer mit Haushalt): der Haushaltsschlüssel.
  shops: {
    name: '',
    shelfOrder: null,
    position: 0,
  },
  event_marks: {
    activity: null,
    activityMin: null,
    cancelled: false,
  },
  // Lesbar bleiben Zeitzone, Sprache, Uhrzeiten, Module, Mahlzeiten, Wasser,
  // Schalter (Erinnerungen, Rückblick, Erfolge, Wochenzahl), Kalorien- und
  // Eiweißziel (Wochenrückblick) und das Gewicht (Kurzbefehl schreibt es).
  profiles: {
    heightCm: null,
    birthYear: null,
    sexFormula: null,
    activityLevel: null,
    goal: null,
    goalRatePct: null,
    carbsTargetG: null,
    fatTargetG: null,
    micros: null,
    wakeTime: '07:00',
  },
  // Standort (27.10.2026): alles außer Haushalt, Verfasser und Zeitstempeln.
  // Orte gehören dem Haushalt, Check-ins ihrem Verfasser — beide immer mit
  // dem Haushaltsschlüssel (`ALWAYS_SEALED_TABLES`).
  places: {
    name: '',
    latitude: null,
    longitude: null,
    radiusM: null,
  },
  location_checkins: {
    kind: null,
    latitude: null,
    longitude: null,
    accuracyM: null,
    placeId: null,
    placeName: null,
    recordedAt: null,
    liveUntil: null,
  },
  // Akte (27.10.2026): alles Inhaltliche — welche Art, alle Felder, der
  // Verweis in die Planung. Lesbar bleiben Verfasser, Haushalt, Person und
  // Sichtbarkeit (`ALWAYS_SEALED_TABLES`). Eigene Akte mit dem
  // Kontoschlüssel, die eines Kindes mit dem Haushaltsschlüssel.
  record_entries: {
    kind: '',
    fields: null,
    plan: null,
  },
  // Haushalts-Essensplan (27.10.2026): Gericht (Name, Zutaten, Nährwerte),
  // Notiz und wer kocht — mit dem Haushaltsschlüssel. Lesbar bleiben
  // Haushalt, Datum, Platz, Wunsch oder Plan und wer ihn angelegt hat.
  household_meals: {
    dish: null,
    note: null,
    cookId: null,
  },
  // Wer isst mit: Portion, was zusätzlich zu kaufen ist, Variante, Hinweis.
  // Lesbar bleiben Eintrag und Person — daran prüft der Server, wer schreibt.
  household_meal_eaters: {
    portions: null,
    items: [] as unknown[],
    variant: null,
    vegetarian: false,
  },
  // Persönliche Abweichungen und Dauerregeln — mit dem Kontoschlüssel.
  // Lesbar bleibt nur der Verweis auf den Eintrag (selber Haushalt).
  meal_deviations: {
    skip: false,
    factor: null,
    extras: [] as unknown[],
    variant: null,
    rules: null,
  },
  // Tagesprotokoll (28.10.2026, PLANUNG.md (6j)): was gegessen wurde — Name,
  // Herkunft (Rezept, Lebensmittel), Menge, Uhrzeit, Kalorien, Kohlenhydrate,
  // Fett, weitere Nährstoffe, Notiz und die Markierung „geschätzt“. Lesbar
  // bleiben Datum und Platz (die Mahlzeiten-Erinnerung erkennt daran „schon
  // eingetragen“) und das Eiweiß (Wochenrückblick: Ø Protein und Ring). Leer
  // ist `null`, nie 0 — eine Zeile ohne Kalorien ist keine mit 0 kcal.
  food_log: {
    eatenAt: null,
    recipeId: null,
    foodId: null,
    label: '',
    portions: null,
    grams: null,
    kcal: null,
    carbs: null,
    fat: null,
    nutrients: null,
    note: null,
    estimate: null,
  },
} as const;

export type EncryptedTable = keyof typeof SEALED_EMPTY;

export const ENCRYPTED_TABLES = Object.keys(SEALED_EMPTY) as EncryptedTable[];

export function isEncryptedTable(table: string): table is EncryptedTable {
  return Object.hasOwn(SEALED_EMPTY, table);
}

/** Die Inhaltsfelder einer verschlüsselten Tabelle. */
export function sealedFields(table: EncryptedTable): string[] {
  return Object.keys(SEALED_EMPTY[table]);
}

/** Stehen im Umschlag nur leere Werte — kein Klartext neben dem Chiffrat? */
export function isSealedEmpty(table: EncryptedTable, row: Record<string, unknown>): boolean {
  const empty = SEALED_EMPTY[table] as Record<string, unknown>;
  return Object.entries(empty).every(([field, value]) => {
    const actual = row[field];
    if (Array.isArray(value))
      return actual === undefined || (Array.isArray(actual) && actual.length === 0);
    return actual === undefined || actual === value;
  });
}

/**
 * Darf der Inhalt einer verschlüsselten Zeile ganz leer sein (27.10.2026)?
 *
 * Nach dem Vorfall vom 26.10. (PLANUNG.md (6f)): Ein Gerät verschlüsselte
 * leere Hüllen neu, und der Server — der den Inhalt nicht lesen kann — nahm
 * sie an. Ein Einkaufsartikel ohne Namen, eine Liste ohne Namen, eine
 * Ausgabe ohne Betrag gibt es aber nicht: Im Klartext wiese das Push-Schema
 * sie ab (`min(1)`). Solche Zeilen hält das Gerät deshalb zurück, statt sie
 * zu verschlüsseln (`isForbiddenEmpty`).
 *
 * - `allowed`: leer ist ein echter Stand (Tagebuchtag nur mit Gewicht, Foto
 *   ohne Unterschrift, Termin ohne Aktivität, Gedanke nur mit Foto).
 * - `forbidden`: leer heißt kaputt.
 * - `withoutKey`: Kategorien — eine feste (`key`) hat keinen Namen, eine
 *   eigene ohne Namen gibt es nicht.
 *
 * Jede Tabelle aus `SEALED_EMPTY` steht hier ausdrücklich (Registertest).
 */
export const EMPTY_CONTENT = {
  journal_notes: 'allowed',
  journal_entries: 'allowed',
  journal_photos: 'allowed',
  recipe_cook_notes: 'allowed',
  expense_categories: 'withoutKey',
  recurring_expenses: 'forbidden',
  expenses: 'forbidden',
  savings_goals: 'forbidden',
  savings_deposits: 'forbidden',
  task_lists: 'forbidden',
  events: 'allowed',
  household_events: 'allowed',
  shopping_items: 'forbidden',
  bucket_items: 'forbidden',
  member_notes: 'forbidden',
  exercises: 'forbidden',
  plan_exercises: 'forbidden',
  shops: 'forbidden',
  // Keine Aktivität, nicht ausgefallen: die Markierung zurückgenommen.
  event_marks: 'allowed',
  // Ein Profil ohne Körperwerte ist der Stand jedes neuen Kontos.
  profiles: 'allowed',
  // Ein Ort ohne Namen, ein Check-in ohne Punkt gibt es nicht.
  places: 'forbidden',
  location_checkins: 'forbidden',
  // Ein Akte-Eintrag ohne Art und Namen gibt es nicht.
  record_entries: 'forbidden',
  // Ein Eintrag ohne Gericht, eine Mitess-Zeile ohne Portion gibt es nicht.
  household_meals: 'forbidden',
  household_meal_eaters: 'forbidden',
  // Eine zurückgenommene Abweichung ist leer — ein echter Stand.
  meal_deviations: 'allowed',
  // Ein Eintrag ohne Namen gibt es nicht (im Klartext `min(1)`).
  food_log: 'forbidden',
} as const satisfies Record<EncryptedTable, 'allowed' | 'forbidden' | 'withoutKey'>;

/** Grund an einem zurückgehaltenen Outbox-Eintrag — kein Fehlversuch. */
export const EMPTY_CONTENT_REASON = 'Leerer Inhalt zurückgehalten';

/**
 * Würde diese Zeile einen Eintrag beim Server leeren? Nur Änderungen, nie
 * Löschungen — eine Löschung trägt beim Server ohnehin keinen Inhalt.
 */
export function isForbiddenEmpty(table: EncryptedTable, row: Record<string, unknown>): boolean {
  if (row.deletedAt) return false;
  const rule: 'allowed' | 'forbidden' | 'withoutKey' = EMPTY_CONTENT[table];
  if (rule === 'allowed') return false;
  if (rule === 'withoutKey' && typeof row.key === 'string') return false;
  return isSealedEmpty(table, row);
}

/**
 * Tabellen, die **nie** als Klartext zum Server gehen (27.10.2026, Standort).
 *
 * Andere geteilte Tabellen gehen lesbar, solange der Haushalt noch keinen
 * Schlüssel hat (nicht alle Erwachsenen haben eingerichtet) — ein Ort ist
 * dafür zu heikel. Ohne Haushaltsschlüssel bleibt ein Check-in in der
 * Outbox (das Gerät hält, die Oberfläche sagt es), und der Server weist
 * Klartext immer ab, nicht erst nach dem Einrichten.
 *
 * Die Akte (27.10.2026) ebenso: Allergien, Medikamente, Versicherungsnummer
 * sind Gesundheitsdaten (DSGVO Art. 9). Die eigene wartet auf den
 * Kontoschlüssel, die eines Kindes auf den Haushaltsschlüssel.
 */
export const ALWAYS_SEALED_TABLES = [
  'places',
  'location_checkins',
  'record_entries',
] as const satisfies readonly EncryptedTable[];

/** Standort — die Ansicht zählt, was davon auf den Haushaltsschlüssel wartet. */
export const LOCATION_SEALED_TABLES = [
  'places',
  'location_checkins',
] as const satisfies readonly (typeof ALWAYS_SEALED_TABLES)[number][];

/** Grund, mit dem der Server Standort im Klartext abweist — nicht `E2E_REQUIRED_REASON`, das hieße „Konto verschlüsselt“. */
export const ALWAYS_SEALED_REASON = 'Standort nur verschlüsselt';
/** Ebenso für die Akte (27.10.2026). */
export const RECORD_SEALED_REASON = 'Akte nur verschlüsselt';

export function isAlwaysSealed(table: string): boolean {
  return (ALWAYS_SEALED_TABLES as readonly string[]).includes(table);
}

/** Grund der Ablehnung von Klartext für eine Tabelle aus `ALWAYS_SEALED_TABLES`. */
export function alwaysSealedReason(table: string): string {
  return table === 'record_entries' ? RECORD_SEALED_REASON : ALWAYS_SEALED_REASON;
}
